<template>
  <div v-if="show" class="evat-verification-qr">
    <div v-if="loading && !displayQr" class="evat-verification-qr__loading">
      <v-progress-circular
        indeterminate
        size="28"
        width="2"
        color="green darken-2"
      ></v-progress-circular>
      <span class="ml-2 text--secondary">Loading GRA verification…</span>
    </div>

    <template v-else-if="displayQr">
      <div class="evat-verification-qr__title">
        <v-icon small color="green darken-2" class="mr-1">
          mdi-check-decagram
        </v-icon>
        GRA E-VAT verified
      </div>
      <qrcode-vue
        :value="displayQr"
        :size="180"
        level="M"
        class="evat-verification-qr__code"
      ></qrcode-vue>
      <div v-if="displayReceipt" class="evat-verification-qr__meta">
        Receipt: <strong>{{ displayReceipt }}</strong>
      </div>
      <a
        :href="displayQr"
        target="_blank"
        rel="noopener"
        class="evat-verification-qr__link"
      >
        Verify on GRA
      </a>
    </template>
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import { isGhanaBusiness } from "../../utils/evat";

export default {
  name: "EvatVerificationQr",
  components: { QrcodeVue },
  props: {
    evat: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      fetchedQr: "",
      fetchedReceipt: "",
      loadedId: null,
      fetchToken: 0,
    };
  },
  computed: {
    submissionId() {
      return this.evat?.submission_id ?? null;
    },
    isFiscalised() {
      return (
        !!this.evat &&
        (this.evat.is_verified === true || this.evat.status === "submitted")
      );
    },
    displayQr() {
      return this.evat?.qr_code || this.fetchedQr || "";
    },
    displayReceipt() {
      return (
        this.evat?.ysdc_receipt_number ||
        this.evat?.receipt_number ||
        this.fetchedReceipt ||
        ""
      );
    },
    show() {
      if (!isGhanaBusiness(this.$store) || !this.evat) return false;
      if (!this.isFiscalised) return false;
      return this.loading || !!this.displayQr;
    },
  },
  watch: {
    evat: {
      deep: true,
      immediate: true,
      handler(val) {
        if (!val || !this.isFiscalised) {
          this.resetFetched();
          return;
        }
        if (val.qr_code) {
          this.loadedId = val.submission_id ?? this.loadedId;
          return;
        }
        if (this.submissionId != null) {
          this.$nextTick(() => this.fetchSubmission());
        }
      },
    },
  },
  methods: {
    resetFetched() {
      this.fetchToken += 1;
      this.loading = false;
      this.fetchedQr = "";
      this.fetchedReceipt = "";
      this.loadedId = null;
    },
    async fetchSubmission() {
      const id = this.submissionId;
      if (!id) return;
      if (this.loadedId === id && this.displayQr) return;

      const token = ++this.fetchToken;
      this.loading = true;

      try {
        const res = await axios.get(`/api/evat/submissions/${id}`);
        if (token !== this.fetchToken) return;

        const submission = res.data?.data || res.data || {};
        this.fetchedQr =
          submission.qr_code ||
          submission.qr ||
          submission.verification_url ||
          "";
        this.fetchedReceipt =
          submission.ysdc_receipt_number ||
          submission.receipt_number ||
          "";
        this.loadedId = id;
      } catch (e) {
        if (token === this.fetchToken) this.loadedId = null;
      } finally {
        if (token === this.fetchToken) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.evat-verification-qr {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  border: 1px solid rgba(76, 175, 80, 0.4);
  border-radius: 10px;
  background: rgba(76, 175, 80, 0.06);
}

.evat-verification-qr__loading {
  display: flex;
  align-items: center;
}

.evat-verification-qr__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 13px;
  color: #2e7d32;
}

.evat-verification-qr__code {
  padding: 6px;
  background: #fff;
  border-radius: 6px;
}

.evat-verification-qr__meta {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.evat-verification-qr__link {
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
}
</style>
